import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getImageUrl } from '../utils/helpers';

function ImageUploader({ 
  onImageUpload, 
  planId, 
  label = "📸 Upload images", 
  accept = "image/*", 
  multiple = true,
  showPreview = true,
  images = [],
  onImageClick = null,
  compact = false,
  className = "",
  style = {}
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);

  const handleFileUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    
    try {
      if (onImageUpload) {
        // Let the parent component handle the upload
        await onImageUpload(files);
      } else if (planId) {
        // Default upload behavior - but this shouldn't be used in our current setup
        const uploadPromises = files.map(file => {
          const formData = new FormData();
          formData.append('image', file);
          return axios.post(`/api/plan/${planId}/upload-image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        });

        const responses = await Promise.all(uploadPromises);
        const imagePaths = responses.map(response => response.data.image_path);
        
        console.log('Uploaded images:', imagePaths);
      }
    } catch (error) {
      console.error('Failed to upload images:', error);
    }
    
    setIsUploading(false);
  }, [planId, compact, onImageUpload]);

  // Handle clipboard paste
  const handlePaste = useCallback((e) => {
    // Only handle paste if this component is currently active (hovered or clicked)
    if (!isActive) {
      return;
    }

    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault();
      handleFileUpload(imageFiles);
    }
  }, [planId, compact, handleFileUpload, isActive]);

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFileUpload(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleClick = () => {
    setIsActive(true);
    fileInputRef.current?.click();
  };

  const handleContainerFocus = () => {
    // Make the container focusable for clipboard paste
    if (containerRef.current) {
      containerRef.current.focus();
    }
  };

  const handleMouseEnter = () => {
    setIsActive(true);
  };

  const handleMouseLeave = () => {
    setIsActive(false);
  };

  if (compact) {
    return (
      <div 
        ref={containerRef}
        className={`image-uploader-compact ${className}`}
        style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          padding: '0.25rem',
          borderRadius: '4px',
          border: isActive ? '2px dashed rgba(59, 130, 246, 0.6) !important' : '2px dashed transparent',
          backgroundColor: isActive ? 'rgba(59, 130, 246, 0.05) !important' : 'transparent',
          transition: 'all 0.2s ease',
          minWidth: '40px',
          minHeight: '32px',
          ...style 
        }}
        tabIndex={0}
        onFocus={handleContainerFocus}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        title={isActive ? "📋 Ready for clipboard paste (Ctrl+V)" : "📸 Upload image"}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsActive(true);
            fileInputRef.current?.click();
          }}
          onFocus={() => setIsActive(true)}
          disabled={isUploading}
                      style={{
              background: isActive ? 'rgba(59, 130, 246, 0.2) !important' : 'none',
              border: isActive ? '2px solid rgba(59, 130, 246, 0.8) !important' : 'none',
            cursor: isUploading ? 'not-allowed' : 'pointer',
            padding: '0.25rem',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            opacity: isUploading ? 0.6 : 1,
            transition: 'all 0.2s ease'
          }}
          title={isActive ? "📋 Ready for clipboard paste (Ctrl+V)" : "📸 Upload image"}
        >
          {isUploading ? '⏳' : (isActive ? '📋' : '📸')}
        </button>
        
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`image-uploader ${className}`}
      style={style}
      tabIndex={0}
      onFocus={handleContainerFocus}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      title={isActive ? "📋 Ready for clipboard paste (Ctrl+V)" : "📸 Upload images"}
    >
      <div 
        className={`image-upload ${isDragging ? 'dragging' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{ 
          border: '2px dashed',
          borderRadius: '8px',
          padding: '1rem',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s',
          backgroundColor: isDragging ? 'rgba(59, 130, 246, 0.1)' : (isActive ? 'rgba(59, 130, 246, 0.05)' : 'transparent'),
          borderColor: isDragging ? 'rgba(59, 130, 246, 0.8)' : (isActive ? 'rgba(59, 130, 246, 0.6)' : 'rgba(156, 163, 175, 0.5)'),
          boxShadow: isActive ? '0 0 0 1px rgba(59, 130, 246, 0.3)' : 'none'
        }}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        <div style={{ marginBottom: '0.5rem' }}>
          {isUploading ? '⏳ Uploading...' : (isActive ? '📋 Ready for clipboard paste!' : label)}
        </div>
        
        <div style={{ fontSize: '0.875rem', color: isActive ? 'rgba(59, 130, 246, 0.9)' : 'rgba(107, 114, 128, 0.8)', fontWeight: isActive ? '500' : 'normal' }}>
          {isActive ? 'Press Ctrl+V (Cmd+V) to paste image from clipboard' : 'Click to browse, drag & drop, or paste from clipboard'}
        </div>
      </div>
      
      {showPreview && images && images.length > 0 && (
        <div className="image-preview" style={{ marginTop: '1rem' }}>
          {images.map((image, index) => (
            <img 
              key={index} 
              src={getImageUrl(image, planId)} 
              alt={`Image ${index + 1}`}
              style={{ 
                cursor: onImageClick ? 'pointer' : 'default',
                maxHeight: '150px',
                margin: '0.25rem'
              }}
              onClick={() => onImageClick && onImageClick(image)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default ImageUploader; 